import { SxProps, Theme } from '@mui/material/styles';
import isEqual from 'lodash/isEqual';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useDeepCompareMemo } from 'use-deep-compare';

import { Breakpoint, Spacing, SpacingKey } from '../../theme/type';
import { getMargin } from '../../utils/spacing';
import { StyledBox, StyledVideo } from './styled';
import { HEIGHTS, MARGIN_FALLBACK, WIDTHS } from './utils';

type VideoSize = {
  maxHeight: string;
  maxWidth: string;
};

type VideoProps = {
  horizontal?: boolean;
  margin?: Spacing;
  mobile?: boolean;
  src: string;
  sx?: SxProps<Theme>;
};

const Video: FC<VideoProps> = ({ horizontal = false, margin, mobile = false, src, sx }) => {
  const [videoSize, setVideoSize] = useState<VideoSize>({ maxHeight: '', maxWidth: '' });
  const [boxMargin, setBoxMargin] = useState<SxProps<Theme>>({});

  const setSize = useCallback(() => {
    const { MD, SM } = Breakpoint;
    const { X, Y } = SpacingKey;
    const md = !mobile && window.innerWidth >= 768;

    const size: VideoSize = {
      maxHeight: md ? HEIGHTS[MD] : horizontal ? HEIGHTS[X] : HEIGHTS[Y],
      maxWidth: md ? WIDTHS[MD] : WIDTHS[SM],
    };

    setVideoSize(prev => (isEqual(prev, size) ? prev : size));
  }, [mobile, horizontal]);

  useEffect(() => {
    setSize();

    window.addEventListener('resize', setSize);

    return (): void => {
      window.removeEventListener('resize', setSize);
    };
  }, [setSize]);

  useEffect(() => {
    const sx = getMargin(margin, MARGIN_FALLBACK) as SxProps<Theme>;
    setBoxMargin((prev: SxProps<Theme>) => (isEqual(prev, sx) ? prev : sx));
  }, [margin]);

  const sxVideo = useDeepCompareMemo<SxProps<Theme>>(
    () => ({ ...sx, ...videoSize }),
    [sx, videoSize]
  );

  return (
    <StyledBox sx={boxMargin}>
      <StyledVideo
        component='video'
        controls={true}
        controlsList='nodownload'
        onContextMenu={() => false}
        poster={`/video/${src}.jpg`}
        sx={sxVideo}
      >
        <source src={`/video/${src}.mp4`} type='video/mp4' />
      </StyledVideo>
    </StyledBox>
  );
};

export default memo(Video);
